import { Node } from "@tiptap/core";

const KINDS = ["link", "url"];
const SIGNATURE = "proposalSignature";

// Where the writing stops: above the signature. A blank last paragraph gets reused.
function writingAreaEnd(doc) {
  let end = doc.content.size;
  doc.forEach((node, offset) => {
    if (node.type.name === SIGNATURE) end = Math.min(end, offset);
  });
  const last = doc.resolve(end).nodeBefore;
  const blank = last?.type.name === "paragraph" && last.content.size === 0;
  return blank ? { from: end - last.nodeSize, to: end } : end;
}

// Keeps the signature a block of its own, which the composer needs to find it again: to
// swap it after an edit, and to add a link above it.
export const ProposalSignature = Node.create({
  name: SIGNATURE,
  group: "block",
  content: "block+",
  defining: true,

  addAttributes() {
    return {
      id: {
        default: null,
        parseHTML: (el) => el.getAttribute("data-signature-id"),
        renderHTML: (attrs) => ({ "data-signature-id": attrs.id })
      }
    };
  },

  parseHTML() {
    return [{ tag: "div[data-signature]" }];
  },

  renderHTML({ HTMLAttributes }) {
    return ["div", { "data-signature": "true", ...HTMLAttributes }, 0];
  }
});

// An atom for the place a customer's link will go. The token does not exist until the
// send, so the composer holds this and Proposals::MergeTags turns it into the address.
// It serializes to the span MergeTags.chip writes, with the label in data-label. Only a
// link has a label to edit: a url resolves to the bare address.
export const ProposalTag = Node.create({
  name: "proposalTag",
  group: "inline",
  inline: true,
  atom: true,
  selectable: true,

  addOptions() {
    return { labels: { linkText: "Link text", remove: "Remove link tag" } };
  },

  // ProseMirror starts with the selection at the top of the body whether or not the rep
  // has put a cursor anywhere, and Tab into the editor does not move it. Only a moved
  // selection says the rep chose a place. setContent forgets it in editor.js.
  addStorage() {
    return { cursorPlaced: false };
  },

  onSelectionUpdate() {
    this.storage.cursorPlaced = true;
  },

  addCommands() {
    return {
      insertProposalTag:
        (content) =>
        ({ chain, editor }) => {
          if (this.storage.cursorPlaced) return chain().focus().insertContent(content).run();

          const paragraph =
            typeof content === "string" ? `<p>${content}</p>` : { type: "paragraph", content: [content] };
          return chain().insertContentAt(writingAreaEnd(editor.state.doc), paragraph).focus().run();
        }
    };
  },

  addAttributes() {
    return {
      kind: { default: "link", parseHTML: (el) => el.getAttribute("data-proposal-tag"), renderHTML: () => ({}) },
      label: {
        default: "",
        parseHTML: (el) => el.getAttribute("data-label") || el.textContent,
        renderHTML: () => ({})
      }
    };
  },

  parseHTML() {
    return [
      {
        tag: "span[data-proposal-tag]",
        getAttrs: (el) => (KINDS.includes(el.getAttribute("data-proposal-tag")) ? {} : false)
      }
    ];
  },

  renderHTML({ node }) {
    return ["span", { "data-proposal-tag": node.attrs.kind, "data-label": node.attrs.label }, node.attrs.label];
  },

  addNodeView() {
    const { labels } = this.options;
    const { storage } = this;

    return ({ node, getPos, editor }) => {
      let attrs = node.attrs;
      const dom = document.createElement("span");
      dom.className =
        "inline-flex items-center gap-0.5 rounded bg-primary-100 ps-2 align-baseline font-medium text-primary-900 " +
        "focus-within:ring-2 focus-within:ring-primary-700 " +
        "dark:bg-primary-900 dark:text-primary-100 dark:focus-within:ring-primary-400 " +
        // A chip selected by the arrow keys hides the caret, so this is all that shows where
        // the keyboard is. It goes with the editor's focus; the selection stays behind.
        "[.ProseMirror-focused_&.ProseMirror-selectednode]:ring-2 " +
        "[.ProseMirror-focused_&.ProseMirror-selectednode]:ring-primary-700 " +
        "dark:[.ProseMirror-focused_&.ProseMirror-selectednode]:ring-primary-400";
      dom.setAttribute("contenteditable", "false");

      let input = null;
      if (node.attrs.kind === "link") {
        input = document.createElement("input");
        input.type = "text";
        input.value = node.attrs.label;
        input.autocomplete = "off";
        input.size = Math.max(input.value.length, 4);
        input.setAttribute("aria-label", labels.linkText);
        input.className =
          "border-0 bg-transparent p-0 text-sm font-medium text-inherit focus:outline-none focus:ring-0";
        input.addEventListener("input", () => {
          input.size = Math.max(input.value.length, 4);
          const label = input.value;
          editor.commands.command(({ tr }) => {
            tr.setNodeMarkup(getPos(), undefined, { ...attrs, label });
            return true;
          });
        });
        // Enter here must not send the whole form.
        input.addEventListener("keydown", (event) => {
          if (event.key !== "Enter") return;
          event.preventDefault();
          editor.commands.focus();
        });
        dom.append(input);
      } else {
        dom.append(document.createTextNode(node.attrs.label));
      }

      const remove = document.createElement("button");
      remove.type = "button";
      remove.textContent = "×";
      remove.setAttribute("aria-label", labels.remove);
      remove.className = "h-6 w-6 rounded text-base leading-none hover:bg-primary-200 dark:hover:bg-primary-800";
      // The cursor stays in the gap, so Add link puts the chip back where it was.
      remove.addEventListener("click", () => {
        const from = getPos();
        storage.cursorPlaced = true;
        editor
          .chain()
          .focus()
          .deleteRange({ from, to: from + 1 })
          .setTextSelection(from)
          .run();
      });
      dom.append(remove);

      return {
        dom,
        // The input and the button handle their own events, not the editor.
        stopEvent: (event) => event.target !== dom,
        ignoreMutation: () => true,
        update(updated) {
          if (updated.type.name !== "proposalTag" || updated.attrs.kind !== node.attrs.kind) return false;
          attrs = updated.attrs;
          if (input && input.value !== attrs.label) input.value = attrs.label;
          return true;
        }
      };
    };
  }
});
